// composer/AudienceMenu.tsx — the composer's audience-selector popover.
//
// Root menu: Everyone / Followers / Specific regions / Specific people.
// Regions and People each drill into a sub-view within the same popover.
//
// 1.27.60 — with a VIDEO attached (the `video` prop) the root menu becomes the video
// visibility list: Channel default (Settings → Privacy → Your channel; stored as the
// plain 'everyone'), Public (everyone, even while the account is private), Followers,
// Specific regions, Specific people, Unlisted (only people with the link).
// No dedicated handle-search endpoint exists yet — the People picker validates
// each typed handle against GET /users/<handle>/ on Add (judgment call, see
// report) rather than offering live-typeahead suggestions.

import { useEffect, useState } from 'react';
import styled from 'styled-components';
import { Popover } from '@/ui';
import { ComposerTool as IconButton } from './ComposerTool';
import { Icon } from '@/icons';
import type { AudienceType } from '@/core/models/post';
import { regionsService, type RegionOption } from '@/core/services/regionsService';
import { usersService } from '@/core/services/usersService';

export const MAX_AUDIENCE_PEOPLE = 50;

const MenuItem = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  padding: 8px 10px;
  border: none;
  border-radius: ${({ theme }) => theme.radius.sm};
  background: transparent;
  color: ${({ theme }) => theme.color.textPrimary};
  font-family: ${({ theme }) => theme.font.body};
  font-size: ${({ theme }) => theme.fontSize.sm};
  cursor: pointer;
  text-align: left;
  white-space: nowrap;

  &:hover {
    background: ${({ theme }) => theme.color.glassBorder};
  }
  &[aria-checked='true'] {
    color: ${({ theme }) => theme.color.accentCyan};
  }
`;

const Panel = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  width: 240px;
  max-height: 320px;
  overflow-y: auto;
`;

const BackRow = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  background: transparent;
  border: none;
  color: ${({ theme }) => theme.color.textMuted};
  font-family: ${({ theme }) => theme.font.body};
  font-size: ${({ theme }) => theme.fontSize.xs};
  cursor: pointer;
  padding: 4px 6px 8px;
`;

const RegionRow = styled.button`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  width: 100%;
  padding: 6px 10px;
  border: none;
  background: transparent;
  font-family: ${({ theme }) => theme.font.body};
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: ${({ theme }) => theme.color.textPrimary};
  text-align: left;
  cursor: pointer;

  &:hover {
    background: ${({ theme }) => theme.color.glassBorder};
  }
  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.color.focusRing};
    outline-offset: -2px;
  }
`;

// Custom check indicator — never a raw <input type="checkbox"> nested in a menu
// (mixes a form control into an ARIA menu, which is invalid; the row itself
// carries role="menuitemcheckbox" + aria-checked instead). Mirrors FeedTabs.tsx.
const CheckBox = styled.span<{ $checked: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  flex-shrink: 0;
  border-radius: ${({ theme }) => theme.radius.sm};
  border: 1px solid ${({ $checked, theme }) => ($checked ? 'transparent' : theme.color.glassBorder)};
  background: ${({ $checked, theme }) => ($checked ? theme.color.accentCyan : 'transparent')};
  color: ${({ theme }) => theme.color.onAccent};
`;

const PeopleInputRow = styled.div`
  display: flex;
  gap: 6px;
  padding: 4px 6px 8px;
`;

const PeopleInput = styled.input`
  flex: 1;
  background: transparent;
  border: 1px solid ${({ theme }) => theme.color.glassBorder};
  border-radius: ${({ theme }) => theme.radius.sm};
  padding: 6px 8px;
  color: ${({ theme }) => theme.color.textPrimary};
  font-family: ${({ theme }) => theme.font.body};
  font-size: ${({ theme }) => theme.fontSize.sm};
`;

const AddBtn = styled.button`
  background: ${({ theme }) => theme.color.accentCyan};
  color: ${({ theme }) => theme.color.onAccent};
  border: none;
  border-radius: ${({ theme }) => theme.radius.sm};
  padding: 0 10px;
  cursor: pointer;
  font-size: ${({ theme }) => theme.fontSize.xs};
  font-weight: 600;

  &:disabled {
    opacity: 0.5;
    cursor: default;
  }
`;

const ChipRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  padding: 0 6px 8px;
`;

const Chip = styled.span<{ $bad?: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  background: ${({ $bad, theme }) =>
    $bad ? `color-mix(in srgb, ${theme.color.danger} 14%, transparent)` : theme.color.glassBorder};
  border: 1px solid transparent;
  border-radius: ${({ theme }) => theme.radius.sm};
  padding: 3px 6px;
  font-size: ${({ theme }) => theme.fontSize.xs};
  color: ${({ $bad, theme }) => ($bad ? theme.color.danger : theme.color.textPrimary)};
`;

const ChipRemove = styled.button`
  background: transparent;
  border: none;
  color: ${({ theme }) => theme.color.textMuted};
  cursor: pointer;
  display: inline-flex;
`;

const ErrorText = styled.p`
  margin: 0;
  padding: 0 6px 6px;
  color: ${({ theme }) => theme.color.danger};
  font-family: ${({ theme }) => theme.font.body};
  font-size: ${({ theme }) => theme.fontSize.xs};
`;

const AUDIENCE_ICON: Record<AudienceType, 'globe' | 'users' | 'map-pin' | 'user-check' | 'link'> = {
  everyone: 'globe',
  followers: 'users',
  regions: 'map-pin',
  people: 'user-check',
  public: 'globe',
  unlisted: 'link',
};

export const AUDIENCE_LABEL: Record<AudienceType, string> = {
  everyone: 'Everyone',
  followers: 'Followers',
  regions: 'Specific regions',
  people: 'Specific people',
  public: 'Public',
  unlisted: 'Unlisted',
};

const Choice = styled.span`
  display: grid;
  gap: 1px;
  white-space: normal;
  small {
    color: ${({ theme }) => theme.color.textMuted};
    font-size: ${({ theme }) => theme.fontSize.xs};
  }
`;

export interface AudienceMenuProps {
  audience: AudienceType;
  regions: string[];
  people: string[];
  /** Server named this handle (e.g. '@bob') as unknown in the last submit attempt —
   *  highlights the matching chip and shows an inline message. */
  badHandle?: string | null;
  onChange: (audience: AudienceType, regions: string[], people: string[]) => void;
  /** Set while a video is attached: `defaultLabel` names what "Channel default" means
   *  for this member right now (e.g. "Public", "Followers only"). */
  video?: { defaultLabel: string };
}

export function AudienceMenu({
  audience,
  regions,
  people,
  badHandle,
  onChange,
  video,
}: AudienceMenuProps) {
  const [open, setOpen] = useState(false);
  const [view, setView] = useState<'root' | 'regions' | 'people'>('root');
  const [regionOptions, setRegionOptions] = useState<RegionOption[]>([]);
  const [personInput, setPersonInput] = useState('');
  const [personError, setPersonError] = useState<string | null>(null);
  const [checking, setChecking] = useState(false);

  const triggerLabel =
    video && audience === 'everyone' ? 'Channel default' : AUDIENCE_LABEL[audience];

  useEffect(() => {
    if (view !== 'regions' || regionOptions.length) return;
    void regionsService.list().then((c) => setRegionOptions(c.regions));
  }, [view, regionOptions.length]);

  function resetView() {
    setView('root');
    setPersonError(null);
    setPersonInput('');
  }

  function pickSimple(type: 'everyone' | 'followers' | 'public' | 'unlisted') {
    onChange(type, [], []);
    setOpen(false);
    resetView();
  }

  function toggleRegion(code: string) {
    const next = regions.includes(code) ? regions.filter((r) => r !== code) : [...regions, code];
    onChange('regions', next, []);
  }

  async function addPerson() {
    const handle = personInput.trim().replace(/^@/, '');
    if (!handle) return;
    if (people.length >= MAX_AUDIENCE_PEOPLE) {
      setPersonError(`You can add up to ${MAX_AUDIENCE_PEOPLE} people.`);
      return;
    }
    if (people.some((p) => p.toLowerCase() === `@${handle}`.toLowerCase())) {
      setPersonInput('');
      return;
    }
    setChecking(true);
    setPersonError(null);
    const user = await usersService.getByHandle(handle);
    setChecking(false);
    if (!user) {
      setPersonError(`@${handle} isn't a TruXSocial account.`);
      return;
    }
    onChange('people', [], [...people, `@${handle}`]);
    setPersonInput('');
  }

  function removePerson(handle: string) {
    onChange(
      'people',
      [],
      people.filter((p) => p !== handle),
    );
  }

  return (
    <Popover
      trigger={
        <IconButton
          icon={AUDIENCE_ICON[audience]}
          label={`${video ? 'Video visibility' : 'Post audience'}: ${triggerLabel}`}
          text={triggerLabel}
          size={18}
        />
      }
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (!next) resetView();
      }}
    >
      <Panel>
        {view === 'root' && video && (
          <>
            <MenuItem
              type="button"
              role="menuitemradio"
              aria-checked={audience === 'everyone'}
              onClick={() => pickSimple('everyone')}
            >
              <Icon name="video" size={16} />
              <Choice>
                Channel default
                <small>{video.defaultLabel}. Change it in Settings → Privacy</small>
              </Choice>
            </MenuItem>
            <MenuItem
              type="button"
              role="menuitemradio"
              aria-checked={audience === 'public'}
              onClick={() => pickSimple('public')}
            >
              <Icon name="globe" size={16} />
              <Choice>
                Public
                <small>Anyone, even if your account is private</small>
              </Choice>
            </MenuItem>
          </>
        )}
        {view === 'root' && (
          <>
            {!video && (
              <MenuItem
                type="button"
                role="menuitemradio"
                aria-checked={audience === 'everyone'}
                onClick={() => pickSimple('everyone')}
              >
                <Icon name="globe" size={16} />
                Everyone
              </MenuItem>
            )}
            <MenuItem
              type="button"
              role="menuitemradio"
              aria-checked={audience === 'followers'}
              onClick={() => pickSimple('followers')}
            >
              <Icon name="users" size={16} />
              Followers
            </MenuItem>
            <MenuItem
              type="button"
              role="menuitemradio"
              aria-checked={audience === 'regions'}
              onClick={() => setView('regions')}
            >
              <Icon name="map-pin" size={16} />
              Specific regions
            </MenuItem>
            <MenuItem
              type="button"
              role="menuitemradio"
              aria-checked={audience === 'people'}
              onClick={() => setView('people')}
            >
              <Icon name="user-check" size={16} />
              Specific people
            </MenuItem>
            {video && (
              <MenuItem
                type="button"
                role="menuitemradio"
                aria-checked={audience === 'unlisted'}
                onClick={() => pickSimple('unlisted')}
              >
                <Icon name="link" size={16} />
                <Choice>
                  Unlisted
                  <small>Only people with the link; never listed</small>
                </Choice>
              </MenuItem>
            )}
          </>
        )}

        {view === 'regions' && (
          <>
            <BackRow type="button" onClick={() => setView('root')}>
              <Icon name="chevron-left" size={14} />
              Back
            </BackRow>
            {regionOptions.map((r) => {
              const checked = regions.includes(r.code);
              return (
                <RegionRow
                  key={r.code}
                  type="button"
                  role="menuitemcheckbox"
                  aria-checked={checked}
                  onClick={() => toggleRegion(r.code)}
                >
                  {r.name}
                  <CheckBox $checked={checked} aria-hidden="true">
                    {checked && <Icon name="check" size={11} />}
                  </CheckBox>
                </RegionRow>
              );
            })}
          </>
        )}

        {view === 'people' && (
          <>
            <BackRow type="button" onClick={() => setView('root')}>
              <Icon name="chevron-left" size={14} />
              Back
            </BackRow>
            {/* role="group" — a menu's direct children must be menuitem-family
                elements; a bare text input + button here would be invalid ARIA. */}
            <PeopleInputRow role="group" aria-label="Add a person">
              <PeopleInput
                type="text"
                placeholder="@handle"
                aria-label="Add a person by handle"
                value={personInput}
                onChange={(e) => setPersonInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    void addPerson();
                  }
                }}
              />
              <AddBtn
                type="button"
                disabled={checking || !personInput.trim()}
                onClick={() => void addPerson()}
              >
                Add
              </AddBtn>
            </PeopleInputRow>
            {personError && <ErrorText role="alert">{personError}</ErrorText>}
            {badHandle && (
              <ErrorText role="alert">
                {badHandle} isn&apos;t a TruXSocial account. Remove or fix it.
              </ErrorText>
            )}
            {people.length > 0 && (
              <ChipRow>
                {people.map((p) => {
                  const bad = badHandle != null && p.toLowerCase() === badHandle.toLowerCase();
                  return (
                    <Chip key={p} $bad={bad}>
                      {p}
                      <ChipRemove
                        type="button"
                        aria-label={`Remove ${p}`}
                        onClick={() => removePerson(p)}
                      >
                        <Icon name="close" size={12} />
                      </ChipRemove>
                    </Chip>
                  );
                })}
              </ChipRow>
            )}
          </>
        )}
      </Panel>
    </Popover>
  );
}
